import { createContext, useContext, useState ,useEffect} from "react";
import PropTypes from "prop-types";
import { loginUser, validateToken, getUserProfile } from "../http/api";
import { initializeRoleBasedPreferences, cleanupAllOldGlobalKeys } from "../Utils/userPreferenceManger";
import {
  clearAllJobs as clearRebalanceJobs,
  clearToastShownThisTab as clearRebalanceToastShown,
} from "../Utils/portfolioRebalanceJobStorage";

const AuthContext = createContext();

// Helper function to decode JWT token
const decodeToken = (token) => {
  try {
    if (!token) return null;
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

// Helper function to extract role from decoded token
const getRoleFromToken = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.role) return "";
  
  // Extract role removing "ROLE_" prefix if present
  return decoded.role.replace(/^ROLE_/, "");
};

export const AuthProvider = ({ children }) => {
  const [storedRole, setStoredRole] = useState(localStorage.getItem("role") || "");
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  // Derive userId directly from the decoded JWT token (prefer token.userId, token.sub, or common fields), not from localStorage.
  // Keep userId in state so components can react to token changes; update it whenever token changes.
  // Export the derived userId via AuthContext so useAuth().userId always reflects the token-decoded value.
  const [userId, setUserId] = useState(() => {
    const token = localStorage.getItem("token");
    const decoded = decodeToken(token);
    return decoded?.userId || decoded?.sub || decoded?.user_id || "";
  });
  const [userProfile, setUserProfile] = useState(() => {
    const savedProfile = localStorage.getItem("userProfile");
    if (savedProfile) {
      try {
        return JSON.parse(savedProfile);
      } catch (error) {
        console.error("Error parsing stored profile:", error);
        // Clear invalid data and logout
        localStorage.removeItem("userProfile");
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        localStorage.removeItem("userId");
        return null;
      }
    }
    return null;
  });

  // Derive decoded token once per render from current token
  const decodedToken = token ? decodeToken(token) : null;

  // Derive userId from token (prefer common fields: userId, sub, user_id)
  const tokenUserId = decodedToken?.userId || decodedToken?.sub || decodedToken?.user_id || "";

  // When token changes, update derived fields (role, custId, sharedBlotter and userId)
  useEffect(() => {
    // Ensure localStorage token matches the authoritative token value (or removed)
    if (token) {
      try { localStorage.setItem("token", token); } catch (e) { /* ignore */ }
    } else {
      try {
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
      } catch (e) { /* ignore */ }
    }

    const dt = token ? decodeToken(token) : null;
    const newUserId = dt?.userId || dt?.sub || dt?.user_id || "";

    // Update userId state and persist canonical userId derived from token
    if (newUserId && newUserId !== userId) {
      setUserId(newUserId);
    }
    try {
      // Always write userId to localStorage if token present (empty string if none)
      if (token) {
        localStorage.setItem("userId", newUserId);
      }
    } catch (e) { /* ignore */ }

    // update storedRole derived from token if needed (keeps previous behavior)
    const newRole = token ? getRoleFromToken(token) : storedRole;
    if (newRole && newRole !== storedRole) {
      setStoredRole(newRole);
      try { localStorage.setItem("role", newRole); } catch (e) { /* ignore */ }
    }
  }, [token]);

  // Derive role from token, fallback to stored role
  const role = token ? getRoleFromToken(token) : storedRole;
  
  // Derive isLoggedIn dynamically from token
  const isLoggedIn = !!token;

  // New: extract custId and sharedBlotter from token (safe)
  const custId = decodedToken?.custId || decodedToken?.customerId || "";
  const sharedBlotter = !!(
    decodedToken &&
    (decodedToken.sharedBlotter === 1 ||
      decodedToken.sharedBlotter === "1" ||
      decodedToken.sharedBlotter === true)
  );

  // Extract isPms and isOms flags from token
  const isPms = !!(
    decodedToken &&
    (decodedToken.isPms === 1 ||
      decodedToken.isPms === "1" ||
      decodedToken.isPms === true)
  );
  const isOms = !!(
    decodedToken &&
    (decodedToken.isOms === 1 ||
      decodedToken.isOms === "1" ||
      decodedToken.isOms === true)
  );

  const setlogin = (data) => {
    // Set token first so derived fields update via useEffect
    try { localStorage.setItem("token", data.token); } catch (e) {}
    setToken(data.token);

    // If login payload includes explicit userId keep localStorage in sync
    if (data.userId) {
      try { localStorage.setItem("userId", data.userId); } catch (e) {}
      setUserId(data.userId);
    } else {
      // If no explicit userId provided, decode from token immediately
      const dt = decodeToken(data.token);
      const newUserId = dt?.userId || dt?.sub || dt?.user_id || "";
      if (newUserId) {
        try { localStorage.setItem("userId", newUserId); } catch (e) {}
        setUserId(newUserId);
      }
    }

    if (data.role) {
      try { localStorage.setItem("role", data.role); } catch (e) {}
      setStoredRole(data.role);
    }

    // Initialize role-based preferences after setting role
    initializeRoleBasedPreferences();

    // Optional: Clean up any remaining old global keys after successful login
    // This ensures a clean state for the user
    try {
      cleanupAllOldGlobalKeys();
    } catch (error) {
      console.warn("Error during cleanup of old preference keys:", error);
    }

    // Start each session with a clean rebalance-job record (per-user/per-session).
    clearRebalanceJobs();
    clearRebalanceToastShown();
  };

  const setProfile = (profileData) => {
    localStorage.setItem("userProfile", JSON.stringify(profileData));
    setUserProfile(profileData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("userId");
    localStorage.removeItem("userProfile");
    // Drop any pending/ready rebalance job so it doesn't leak across sessions.
    clearRebalanceJobs();
    clearRebalanceToastShown();
    setStoredRole("");
    setToken("");
    setUserId("");
    setUserProfile(null);
  };

  // Function to fetch user profile
  const fetchProfile = async () => {
    try {
     
      const profileData = await getUserProfile(token);
      if(profileData.status === 403 || !profileData){
        // logout();
        return;
      }
      setProfile(profileData); 
      //if user id  or token is missing then also set them and persist them in local storage
      if (!userId) {
        localStorage.setItem("userId", profileData.userId);
        setUserId(profileData.userId);
      }
      // Don't override role from token with profile role
      if (!storedRole && profileData.role) {
        localStorage.setItem("role", profileData.role);
        setStoredRole(profileData.role);
      }
      
    } catch (error) {
      console.error("Error fetching profile:", error);
      logout(); // Logout if profile fetch fails (e.g., token expired)
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchProfile(); // Fetch profile only if not already stored
      // Ensure role-based preferences are initialized
      initializeRoleBasedPreferences();
    }
  }, [isLoggedIn]);

  return (
    <AuthContext.Provider value={{
      logout,
      role,
      token,
      userId,            // exported userId now derived from token
      userProfile,
      isLoggedIn,
      setlogin,
      setProfile,
      // NEW exports for other modules to consume
      custId,
      sharedBlotter,
      isPms,
      isOms,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  return useContext(AuthContext);
};